import { useEffect, useMemo, useState } from "react";
import {
  Search,
  Plus,
  Home,
  Clock,
  Star,
  Trash2,
  ChevronDown,
  ChevronRight,
  Folder as FolderIcon,
  LayoutGrid,
  List,
  ArrowUpRight,
  Copy,
  Pencil,
  MoveRight,
  Users,
  LogOut,
  PanelLeftClose,
  Check,
  GripVertical,
} from "lucide-react";
import { useApi, post, patch } from "./api";
import { Avatar, Empty, Logo, Menu, MenuItem, Modal, SYMBOLS } from "./ui";
import { ShareDialog } from "./ShareDialog";
import type { Board, Folder, Session, Space, Member } from "../shared/model";
type LibraryData = { boards: Board[]; spaces: Space[]; folders: Folder[] };
type View = {
  kind: "home" | "recent" | "starred" | "trash" | "space" | "folder";
  id?: string;
};
export function Library({
  session,
  go,
  signOut,
}: {
  session: Session;
  go: (p: string) => void;
  signOut: () => void;
}) {
  const api = useApi();
  const [data, setData] = useState<LibraryData>({
      boards: [],
      spaces: [],
      folders: [],
    }),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [query, setQuery] = useState(""),
    [view, setView] = useState<View>({ kind: "home" }),
    [layout, setLayout] = useState(
      localStorage.getItem("ply:layout") || "grid",
    ),
    [sort, setSort] = useState("updated"),
    [expanded, setExpanded] = useState<string[]>([]),
    [busy, setBusy] = useState(false),
    [edit, setEdit] = useState<Board>(),
    [share, setShare] = useState<Board>(),
    [move, setMove] = useState<Board>(),
    [spaceDialog, setSpaceDialog] = useState<Space | "new">(),
    [folderDialog, setFolderDialog] = useState<{
      space: string;
      parent?: string;
      folder?: Folder;
    }>(),
    [membersOpen, setMembersOpen] = useState(false);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (
        e.key === "/" &&
        !(e.target as HTMLElement).closest(
          "input,textarea,[contenteditable=true],[role=dialog]",
        )
      ) {
        e.preventDefault();
        document
          .querySelector<HTMLInputElement>('[aria-label="Search boards"]')
          ?.focus();
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);
  const load = () =>
    api
      .request<LibraryData>("/api/library")
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  useEffect(() => {
    void load();
  }, []);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const create = () =>
    void run(async () => {
      const folder = data.folders.find(
          (f) => view.kind === "folder" && f.id === view.id,
        ),
        space = view.kind === "space" ? view.id : folder?.space_id;
      const b = await api.request<{ id: string }>(
        "/api/boards",
        post({ spaceId: space, folderId: folder?.id }),
      );
      go(`/board/${b.id}`);
    });
  const toggle = (id: string) =>
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const title =
    view.kind === "home"
      ? "All boards"
      : view.kind === "recent"
        ? "Recently opened"
        : view.kind === "starred"
          ? "Starred boards"
          : view.kind === "trash"
            ? "Trash"
            : view.kind === "space"
              ? data.spaces.find((s) => s.id === view.id)?.name
              : data.folders.find((f) => f.id === view.id)?.name;
  const boards = useMemo(
    () =>
      data.boards
        .filter(
          (b) =>
            (view.kind === "trash" ? !!b.deleted_at : !b.deleted_at) &&
            (view.kind !== "starred" || b.starred) &&
            (view.kind !== "recent" || b.opened_at) &&
            (view.kind !== "space" || b.space_id === view.id) &&
            (view.kind !== "folder" || b.folder_id === view.id) &&
            `${b.title} ${b.description}`
              .toLowerCase()
              .includes(query.toLowerCase()),
        )
        .sort((a, b) =>
          sort === "name"
            ? a.title.localeCompare(b.title)
            : sort === "created"
              ? b.created_at.localeCompare(a.created_at)
              : sort === "opened" || view.kind === "recent"
                ? (b.opened_at || "").localeCompare(a.opened_at || "")
                : b.updated_at.localeCompare(a.updated_at),
        ),
    [data, view, query, sort],
  );
  const drop = (
    e: React.DragEvent,
    space: string | null,
    folder: string | null,
  ) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("application/ply-board");
    if (id)
      void run(() =>
        api.request(
          `/api/boards/${id}`,
          patch({ space_id: space, folder_id: folder }),
        ),
      );
  };
  const folderTree = (
    space: string,
    parent: string | null,
    depth = 0,
  ): React.ReactNode =>
    data.folders
      .filter((f) => f.space_id === space && f.parent_id === parent)
      .map((f) => (
        <div key={f.id}>
          <div
            className={`tree-row ${view.id === f.id ? "active" : ""}`}
            style={{ paddingLeft: 18 + depth * 14 }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => drop(e, space, f.id)}
          >
            <button
              className="tree-select"
              onClick={() => {
                setView({ kind: "folder", id: f.id });
                if (!expanded.includes(f.id)) toggle(f.id);
              }}
            >
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(f.id);
                }}
              >
                {expanded.includes(f.id) ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
              </span>
              <FolderIcon size={16} />
              <span>{f.name}</span>
            </button>
            <Menu label={`Options for ${f.name}`}>
              <MenuItem
                onClick={() =>
                  setFolderDialog({
                    space,
                    parent: f.parent_id || undefined,
                    folder: f,
                  })
                }
              >
                <Pencil size={15} />
                Rename folder
              </MenuItem>
              <MenuItem
                onClick={() => setFolderDialog({ space, parent: f.id })}
              >
                <Plus size={15} />
                New subfolder
              </MenuItem>
            </Menu>
          </div>
          {expanded.includes(f.id) && folderTree(space, f.id, depth + 1)}
        </div>
      ));
  const boardMenu = (b: Board) => (
    <Menu label={`Options for ${b.title}`}>
      {b.deleted_at ? (
        <MenuItem
          onClick={() =>
            void run(() => api.request(`/api/boards/${b.id}/restore`, post()))
          }
        >
          <Clock size={15} />
          Restore board
        </MenuItem>
      ) : (
        <>
          <MenuItem onClick={() => go(`/board/${b.id}`)}>
            <ArrowUpRight size={15} />
            Open board
          </MenuItem>
          <MenuItem disabled={b.role === "viewer"} onClick={() => setEdit(b)}>
            <Pencil size={15} />
            Edit details & symbol
          </MenuItem>
          <MenuItem
            onClick={() =>
              void run(async () => {
                const n = await api.request<{ id: string }>(
                  `/api/boards/${b.id}/duplicate`,
                  post(),
                );
                go(`/board/${n.id}`);
              })
            }
          >
            <Copy size={15} />
            Duplicate
          </MenuItem>
          <MenuItem disabled={b.role === "viewer"} onClick={() => setMove(b)}>
            <MoveRight size={15} />
            Move to…
          </MenuItem>
          {b.role === "owner" && (
            <MenuItem onClick={() => setShare(b)}>
              <Users size={15} />
              Share
            </MenuItem>
          )}
          <MenuItem
            disabled={b.role !== "owner"}
            danger
            onClick={() =>
              void run(() =>
                api.request(`/api/boards/${b.id}`, { method: "DELETE" }),
              )
            }
          >
            <Trash2 size={15} />
            Move to Trash
          </MenuItem>
        </>
      )}
    </Menu>
  );
  return (
    <div className="library">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <Logo />
          <span className="internal-label">INTERNAL</span>
        </div>
        <div className="workspace">
          <Avatar name="Renn Media" color="#f0dc78" size={35} />
          <div>
            <b>Renn Media</b>
            <small>A space to think together</small>
          </div>
        </div>
        <nav>
          {(
            [
              { kind: "home", label: "All boards", icon: Home },
              { kind: "recent", label: "Recent", icon: Clock },
              { kind: "starred", label: "Starred", icon: Star },
              { kind: "trash", label: "Trash", icon: Trash2 },
            ] as const
          ).map((n) => (
            <button
              key={n.kind}
              className={`nav-item ${view.kind === n.kind ? "active" : ""}`}
              onClick={() => setView({ kind: n.kind })}
            >
              <n.icon size={18} />
              {n.label}
              {n.kind === "home" && (
                <span className="count">
                  {data.boards.filter((b) => !b.deleted_at).length}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="sidebar-heading">
          <span>SPACES</span>
          <button
            className="icon-button"
            aria-label="New space"
            onClick={() => setSpaceDialog("new")}
          >
            <Plus size={17} />
          </button>
        </div>
        <div className="space-tree">
          {data.spaces.map((s) => (
            <div key={s.id}>
              <div
                className={`tree-row ${view.id === s.id ? "active" : ""}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => drop(e, s.id, null)}
              >
                <button
                  className="tree-select"
                  onClick={() => {
                    setView({ kind: "space", id: s.id });
                    if (!expanded.includes(s.id)) toggle(s.id);
                  }}
                >
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(s.id);
                    }}
                  >
                    {expanded.includes(s.id) ? (
                      <ChevronDown size={13} />
                    ) : (
                      <ChevronRight size={13} />
                    )}
                  </span>
                  <span className="space-symbol">{s.symbol}</span>
                  <span>{s.name}</span>
                </button>
                <Menu label={`Options for ${s.name}`}>
                  <MenuItem onClick={() => setSpaceDialog(s)}>
                    <Pencil size={15} />
                    Edit space
                  </MenuItem>
                  <MenuItem onClick={() => setFolderDialog({ space: s.id })}>
                    <Plus size={15} />
                    New folder
                  </MenuItem>
                </Menu>
              </div>
              {expanded.includes(s.id) && folderTree(s.id, null)}
            </div>
          ))}
          {!data.spaces.length && (
            <button
              className="create-space"
              onClick={() => setSpaceDialog("new")}
            >
              <Plus size={16} />
              Create your first space
            </button>
          )}
        </div>
        <div className="sidebar-bottom">
          <div className="workspace-tip">
            <span>SPACE FOR WHAT’S NEXT</span>
            <p>
              Good ideas need a little
              <br />
              room to grow.
            </p>
            <div className="tip-dots">
              ✳ <span>↗</span> ✧
            </div>
          </div>
          {session.user.role === "admin" && (
            <button className="nav-item" onClick={() => setMembersOpen(true)}>
              <Users size={18} />
              Workspace members
            </button>
          )}
          <div className="profile">
            <Avatar name={session.user.name} />
            <div>
              <b>{session.user.name}</b>
              <small>
                {session.user.role === "admin"
                  ? "Workspace admin"
                  : "Team member"}
              </small>
            </div>
            <button
              className="icon-button"
              aria-label="Sign out"
              onClick={signOut}
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>
      <main className="library-main">
        <header className="library-top">
          <span>
            YOUR WORKSPACE <span className="crumb">/</span> {title}
          </span>
          <div>
            <span className="team-pill">
              <span className="green-dot" />
              Renn Media
            </span>
            <Avatar name={session.user.name} />
          </div>
        </header>
        <div className="library-content">
          <div className="welcome">
            <div>
              <span className="eyebrow">A LITTLE ROOM FOR BIG IDEAS</span>
              <h1>
                {view.kind === "home"
                  ? `Make room for what’s next${session.user.name.startsWith("Renn") ? ", Renn" : ""}.`
                  : title}
              </h1>
              <p>
                {view.kind === "trash"
                  ? "Changed your mind? Bring a board back whenever you need it."
                  : "Gather your thoughts, connect the dots, and build on them together."}
              </p>
            </div>
            {view.kind !== "trash" && (
              <button
                className="button primary"
                onClick={create}
                disabled={busy}
              >
                <Plus size={18} />
                New board
              </button>
            )}
          </div>
          {view.kind === "home" && !query && (
            <button className="quick-create" onClick={create}>
              <div className="quick-preview">
                <Plus size={27} />
              </div>
              <div>
                <b>Start with a blank canvas</b>
                <p>No right way to begin. Just a little space to think.</p>
              </div>
              <ArrowUpRight size={22} />
              <div className="quick-decoration">
                <span>↗</span>
                <span>✳</span>
              </div>
            </button>
          )}
          <div className="board-heading">
            <h2>
              {title}
              <span>{boards.length}</span>
            </h2>
            <div className="view-toggle">
              <button
                aria-label="Grid view"
                className={layout === "grid" ? "active" : ""}
                onClick={() => {
                  setLayout("grid");
                  localStorage.setItem("ply:layout", "grid");
                }}
              >
                <LayoutGrid size={17} />
              </button>
              <button
                aria-label="List view"
                className={layout === "list" ? "active" : ""}
                onClick={() => {
                  setLayout("list");
                  localStorage.setItem("ply:layout", "list");
                }}
              >
                <List size={19} />
              </button>
            </div>
          </div>
          <div className="library-controls">
            <div className="search">
              <Search size={17} />
              <input
                placeholder="Search boards"
                aria-label="Search boards"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <kbd>/</kbd>
            </div>
            <label className="sort">
              Sort by{" "}
              <select
                aria-label="Sort boards"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="updated">Last modified</option>
                <option value="opened">Last opened</option>
                <option value="name">Name</option>
                <option value="created">Date created</option>
              </select>
            </label>
          </div>
          {error && (
            <div role="alert" className="error-bar">
              {error}
              <button onClick={() => setError("")}>Dismiss</button>
            </div>
          )}
          {loading ? (
            <div className="board-grid">
              {[0, 1, 2].map((n) => (
                <div className="skeleton-card" key={n} />
              ))}
            </div>
          ) : boards.length ? (
            <div className={layout === "grid" ? "board-grid" : "board-list"}>
              {layout === "list" && (
                <div className="list-head">
                  <span>Name</span>
                  <span>Space</span>
                  <span>Last modified</span>
                  <span>Owner</span>
                  <span />
                </div>
              )}
              {boards.map((b) => (
                <article
                  key={b.id}
                  className={`board-card ${layout === "list" ? "row" : ""}`}
                  draggable={!b.deleted_at && b.role !== "viewer"}
                  onDragStart={(e) =>
                    e.dataTransfer.setData("application/ply-board", b.id)
                  }
                >
                  {layout === "grid" && (
                    <button
                      className="board-preview"
                      aria-label={`Open ${b.title}`}
                      onClick={() => !b.deleted_at && go(`/board/${b.id}`)}
                    >
                      {b.thumbnail ? (
                        <img src={b.thumbnail} alt="Board preview" />
                      ) : (
                        <div className="blank-preview">
                          <span>{b.symbol}</span>
                          <span className="preview-line" />
                          <span className="preview-line short" />
                        </div>
                      )}
                      <span className="preview-access">
                        {b.team_role ? "Team board" : "Private board"}
                      </span>
                    </button>
                  )}
                  <div className="card-info">
                    <span className="board-symbol">{b.symbol}</span>
                    <button
                      className="board-name"
                      onClick={() => !b.deleted_at && go(`/board/${b.id}`)}
                    >
                      <b>{b.title}</b>
                      <small>
                        {layout === "grid"
                          ? `Edited ${relative(b.updated_at)}`
                          : b.description || "A little space to think"}
                      </small>
                    </button>
                    {layout === "grid" && (
                      <button
                        className={`icon-button star ${b.starred ? "starred" : ""}`}
                        aria-label={b.starred ? "Unstar board" : "Star board"}
                        onClick={() =>
                          void run(() =>
                            api.request(
                              `/api/boards/${b.id}/star`,
                              post({ starred: !b.starred }),
                            ),
                          )
                        }
                      >
                        <Star
                          size={17}
                          fill={b.starred ? "currentColor" : "none"}
                        />
                      </button>
                    )}
                    {layout === "grid" && boardMenu(b)}
                  </div>
                  {layout === "list" && (
                    <>
                      <span className="row-space">
                        {data.spaces.find((s) => s.id === b.space_id)?.name ||
                          "—"}
                      </span>
                      <span className="row-date">{relative(b.updated_at)}</span>
                      <span className="row-owner">
                        <Avatar name={b.owner_name || "Teammate"} size={23} />
                        {b.owner_name || "Teammate"}
                      </span>
                      <div className="row-actions">
                        <button
                          className={`icon-button star ${b.starred ? "starred" : ""}`}
                          aria-label={b.starred ? "Unstar board" : "Star board"}
                          onClick={() =>
                            void run(() =>
                              api.request(
                                `/api/boards/${b.id}/star`,
                                post({ starred: !b.starred }),
                              ),
                            )
                          }
                        >
                          <Star
                            size={16}
                            fill={b.starred ? "currentColor" : "none"}
                          />
                        </button>
                        {boardMenu(b)}
                      </div>
                    </>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <Empty
              title={
                query
                  ? "No matching boards"
                  : view.kind === "trash"
                    ? "Nothing in Trash"
                    : view.kind === "starred"
                      ? "Keep your favourites close"
                      : "Your next idea starts here"
              }
              description={
                query
                  ? "Try another name or a phrase from a board description."
                  : view.kind === "trash"
                    ? "Boards you remove will live here until you restore them."
                    : view.kind === "starred"
                      ? "Star a board to find it here."
                      : "Create a board and give your thoughts some room."
              }
            >
              {!query && view.kind !== "trash" && (
                <button className="button primary" onClick={create}>
                  <Plus size={17} />
                  Create a board
                </button>
              )}
            </Empty>
          )}
          <footer className="library-footer">
            <span>Ply · Made for Renn Media</span>
            <span>Room to think. Space to make.</span>
          </footer>
        </div>
      </main>
      {edit && (
        <BoardDetails
          board={edit}
          onClose={() => setEdit(undefined)}
          onSave={async (input) => {
            await api.request(`/api/boards/${edit.id}`, patch(input));
            setEdit(undefined);
            await load();
          }}
        />
      )}
      {share && (
        <ShareDialog
          board={share}
          onClose={() => {
            setShare(undefined);
            void load();
          }}
        />
      )}
      {move && (
        <Modal
          title="Move board"
          description={move.title}
          onClose={() => setMove(undefined)}
        >
          <LocationForm
            data={data}
            board={move}
            onSave={async (input) => {
              await run(() =>
                api.request(`/api/boards/${move.id}`, patch(input)),
              );
              setMove(undefined);
            }}
          />
        </Modal>
      )}
      {spaceDialog && (
        <Modal
          title={spaceDialog === "new" ? "New space" : "Edit space"}
          description="Keep a channel or project’s boards together."
          onClose={() => setSpaceDialog(undefined)}
        >
          <NameForm
            name={spaceDialog === "new" ? "" : spaceDialog.name}
            symbol={spaceDialog === "new" ? "◈" : spaceDialog.symbol}
            onSave={async (name, symbol) => {
              await run(() =>
                api.request(
                  spaceDialog === "new"
                    ? "/api/spaces"
                    : `/api/spaces/${spaceDialog.id}`,
                  spaceDialog === "new"
                    ? post({ name, symbol })
                    : patch({ name, symbol }),
                ),
              );
              setSpaceDialog(undefined);
            }}
          />
        </Modal>
      )}
      {folderDialog && (
        <Modal
          title={folderDialog.folder ? "Rename folder" : "New folder"}
          description="A little organisation goes a long way."
          onClose={() => setFolderDialog(undefined)}
        >
          <NameForm
            name={folderDialog.folder?.name || ""}
            onSave={async (name) => {
              await run(() =>
                api.request(
                  folderDialog.folder
                    ? `/api/folders/${folderDialog.folder.id}`
                    : "/api/folders",
                  folderDialog.folder
                    ? patch({ name })
                    : post({
                        name,
                        spaceId: folderDialog.space,
                        parentId: folderDialog.parent,
                      }),
                ),
              );
              setExpanded((prev) => [
                ...prev,
                folderDialog.space,
                ...(folderDialog.parent ? [folderDialog.parent] : []),
              ]);
              setFolderDialog(undefined);
            }}
          />
        </Modal>
      )}
      {membersOpen && (
        <Members session={session} onClose={() => setMembersOpen(false)} />
      )}
    </div>
  );
}
function relative(s: string) {
  const d = Date.now() - new Date(s).getTime();
  return d < 60000
    ? "just now"
    : d < 3600000
      ? `${Math.floor(d / 60000)}m ago`
      : d < 86400000
        ? `${Math.floor(d / 3600000)}h ago`
        : new Date(s).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
          });
}
export function BoardDetails({
  board,
  onClose,
  onSave,
}: {
  board: Board;
  onClose: () => void;
  onSave: (input: Partial<Board>) => Promise<void>;
}) {
  const [title, setTitle] = useState(board.title),
    [symbol, setSymbol] = useState(board.symbol),
    [description, setDescription] = useState(board.description),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <Modal
      title="Board details"
      description="Give this idea a name and a little personality."
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void onSave({ title, symbol, description })
            .catch((e) => setError(e.message))
            .finally(() => setBusy(false));
        }}
      >
        <label>
          Board name
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={160}
          />
        </label>
        <label>
          Symbol
          <input
            aria-label="Custom board symbol"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            maxLength={16}
          />
        </label>
        <div className="symbol-grid">
          {SYMBOLS.map((s) => (
            <button
              type="button"
              className={s === symbol ? "selected" : ""}
              key={s}
              onClick={() => setSymbol(s)}
            >
              {s}
            </button>
          ))}
        </div>
        <label>
          Description
          <textarea
            placeholder="What’s this board for?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
          />
        </label>
        {error && <p className="error-text">{error}</p>}
        <div className="dialog-footer">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button disabled={busy} className="button primary">
            Save changes
          </button>
        </div>
      </form>
    </Modal>
  );
}
function NameForm({
  name: initial,
  symbol: initialSymbol,
  onSave,
}: {
  name: string;
  symbol?: string;
  onSave: (name: string, symbol?: string) => Promise<void>;
}) {
  const [name, setName] = useState(initial),
    [symbol, setSymbol] = useState(initialSymbol),
    [error, setError] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(name, symbol).catch((e) => setError(e.message));
      }}
    >
      <label>
        Name
        <input
          autoFocus
          required
          maxLength={160}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      {symbol !== undefined && (
        <label>
          Symbol
          <input
            value={symbol}
            maxLength={16}
            onChange={(e) => setSymbol(e.target.value)}
          />
        </label>
      )}
      {error && <p className="error-text">{error}</p>}
      <div className="dialog-footer">
        <button className="button primary">Save</button>
      </div>
    </form>
  );
}
function LocationForm({
  data,
  board,
  onSave,
}: {
  data: LibraryData;
  board: Board;
  onSave: (input: Partial<Board>) => Promise<void>;
}) {
  const [space, setSpace] = useState(board.space_id || ""),
    [folder, setFolder] = useState(board.folder_id || "");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSave({ space_id: space || null, folder_id: folder || null });
      }}
    >
      <label>
        Space
        <select
          value={space}
          onChange={(e) => {
            setSpace(e.target.value);
            setFolder("");
          }}
        >
          <option value="">No space</option>
          {data.spaces.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Folder
        <select value={folder} onChange={(e) => setFolder(e.target.value)}>
          <option value="">Space root</option>
          {data.folders
            .filter((f) => f.space_id === space)
            .map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
        </select>
      </label>
      <div className="dialog-footer">
        <button className="button primary">Move board</button>
      </div>
    </form>
  );
}
function Members({
  onClose,
  session,
}: {
  onClose: () => void;
  session: Session;
}) {
  const api = useApi(),
    [members, setMembers] = useState<Member[]>([]),
    [email, setEmail] = useState(""),
    [notice, setNotice] = useState(""),
    [error, setError] = useState(""),
    [removing, setRemoving] = useState<Member>(),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    void api
      .request<Member[]>("/api/members")
      .then(setMembers)
      .catch((e) => setError(e.message));
  }, []);
  return (
    <Modal
      title="Renn Media members"
      description="Invite teammates to think together."
      onClose={onClose}
    >
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          void api
            .request("/api/invitations", post({ email }))
            .then(() => {
              setNotice("Invitation sent");
              setEmail("");
            })
            .catch((e) => setError(e.message));
        }}
      >
        <input
          aria-label="Teammate email"
          type="email"
          placeholder="teammate@rennmedia.org"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button className="button primary">Invite</button>
      </form>
      {notice && <p className="success-text">{notice}</p>}
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
      <div className="member-list">
        {members.map((m) => (
          <div className="member-row" key={m.id}>
            <Avatar name={m.name} />
            <div>
              <b>{m.name}</b>
              <small>{m.email}</small>
            </div>
            <span className="pill">{m.role}</span>
            {m.id !== session.user.id && (
              <button
                className="button text-button danger small"
                onClick={() => setRemoving(m)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
      {removing && (
        <div className="share-section">
          <p>
            Remove {removing.name} from Renn Media? Their open boards will
            disconnect. An admin can invite them again.
          </p>
          <div className="dialog-footer">
            <button
              className="button secondary"
              disabled={busy}
              onClick={() => setRemoving(undefined)}
            >
              Keep member
            </button>
            <button
              className="button primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError("");
                try {
                  await api.request(`/api/members/${removing.id}`, {
                    method: "DELETE",
                  });
                  setMembers(members.filter((m) => m.id !== removing.id));
                  setRemoving(undefined);
                  setNotice("Member removed");
                } catch (e) {
                  setError((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Remove member
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
